import React, { useState } from "react";

import Button from "../button";
import shouldHighlight from "./helpers/shouldHighlight";
import { usePartnerContext } from "../../context/partner";
import { PartnerColors } from "../../context/partner/types";

export type TextAreaProps = {
  rows: number;
};

export type TextProps = {
  name?: string;
  value?: string;
  className?: string;
  onChange?: React.ChangeEventHandler<any> | undefined;
  correctAnswer?: string;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  width?: string;
  type?: string;
  textAreaProps?: TextAreaProps;
  highlightColor?: keyof PartnerColors;
  clearable?: boolean;
  autoComplete?: HTMLInputElement["autocomplete"];
};

export default function Text({
  name,
  clearable = false,
  value,
  correctAnswer,
  disabled = false,
  autoComplete,
  className = "p-3 border border-solid rounded-md w-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1",
  readOnly,
  placeholder,
  onChange,
  width = "w-full",
  label,
  type = "text",
  textAreaProps,
  highlightColor,
}: TextProps) {
  const { colors } = usePartnerContext();

  const [focus, setFocus] = useState(false);
  const { rows } = textAreaProps || { rows: 6 };

  const highlightResult = shouldHighlight(
    value,
    correctAnswer,
    colors,
    highlightColor
  );
  const borderColor = highlightResult.color;
  const isHighlighted = highlightResult.name !== "default";

  const inputStyles = {
    borderColor: focus ? colors.borderFocus || "#3b82f6" : borderColor,
    backgroundColor: disabled ? colors.gray50 || colors.gray1 : colors.white,
    boxShadow:
      focus && !isHighlighted
        ? "0 0 0 3px rgba(59, 130, 246, 0.1)"
        : undefined,
  };

  let component;

  if (type === "textarea") {
    component = (
      <textarea
        value={value || ""}
        rows={rows}
        disabled={disabled || readOnly}
        style={inputStyles}
        className={className}
        placeholder={placeholder || label}
        onChange={onChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      />
    );
  } else {
    component = (
      <input
        value={value || ""}
        onBlur={() => {
          setFocus(false);
        }}
        onFocus={() => {
          setFocus(true);
        }}
        disabled={disabled || readOnly}
        style={inputStyles}
        className={className}
        type={type}
        placeholder={placeholder || label}
        onChange={onChange}
        autoComplete={autoComplete}
      />
    );
  }

  return (
    <div className={`${width} flex flex-col space-y-2 justify-evenly`}>
      {label && <p className={"font-semibold text-sm"}>{`${label}`}</p>}
      <div className={"flex flex-row"}>
        {component}
        {clearable && value && (
          <div
            style={{
              position: "relative",
              width: 0,
              left: "-35px",
              top: "12px",
            }}
          >
            <Button
              style={{ position: "relative", padding: "4px" }}
              noShadow
              color={"white"}
              iconColor={"textSecondary"}
              icon={"close"}
              onClick={() => {
                if (onChange) {
                  onChange({ target: { value: "" } } as any);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
